import { useMemo, useEffect } from "react";
import { useQuery } from "react-query";
import { parse, format, subDays, addDays, parseISO } from "date-fns";
import useScheduledMatchRefetch from "../../hooks/useScheduledMatchRefetch";

import { useReactAppState } from "../utils";
import { mlsGetMatches } from "../../api/variables";
import { fetchClient } from "../../api/fetching";
import { formatStatsAPIMatches } from "../../../mls-horizontal-scoreboard/utils";
import { filterMlsMatchesByCompetitionsToSkip } from "../../../mls-match-list/utils";
import useMLSMatch from "../../../mls-match-list/hooks/useMLSMatch";

function MatchHandler({ children, queryClient }) {
  const { apiList } = useReactAppState();

  const { forgeMatchDate, matchSportecId, seasonSportecId } = useMemo(() => {
    return {
      forgeMatchDate: window?.forgeVariables?.match?.matchDateTime,
      matchSportecId: window?.forgeVariables?.match?.sportecId,
      seasonSportecId: window?.forgeVariables?.match?.season,
    };
  }, []);

  const { from, to } = useMemo(() => {
    const { dayBefore: from, dayAfter: to } = parseDateWithBeforeAndAfter(
      forgeMatchDate
    );

    return { from, to };
  }, [forgeMatchDate]);

  const { data: mlsMatch, isError: mlsMatchError } = useMLSMatch({
    matchSportecId,
  });

  const mlsMatchesEndpoint = mlsGetMatches({ from, to, seasonSportecId });

  const { data: mlsMatchByDate } = useQuery({
    queryKey: ["mls-matches-date-range", { from, to }],
    queryFn: () =>
      fetchClient({ endpoint: mlsMatchesEndpoint, apiURL: apiList?.statsAPI }),
    refetchOnWindowFocus: false,
    select: (response) => {
      if (response?.schedule) {
        const sortedMatches = [
          ...response?.schedule?.sort(
            (a, b) =>
              parseISO(a.planned_kickoff_time) -
              parseISO(b.planned_kickoff_time)
          ),
        ];
        const matches = formatStatsAPIMatches(
          filterMlsMatchesByCompetitionsToSkip(sortedMatches)
        );

        const match = matches.find(
          (match) => match.match_id === matchSportecId
        );

        if (match) {
          return match;
        }
      }

      return [];
    },
    enabled: !!from && !!to && mlsMatchError,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (mlsMatchError && mlsMatchByDate) {
      queryClient.setQueryData(["mls-match", matchSportecId], mlsMatchByDate);
    }
  }, [mlsMatchByDate, matchSportecId, mlsMatchError]);

  // Use our custom hook to schedule refetches based on match date
  // Only enabled when we have an error with the primary match data source
  useScheduledMatchRefetch({
    matchDate: mlsMatchByDate?.planned_kickoff_time,
    minutesBefore: 60, // 1 hour before match
    queryKey: ["mls-match", matchSportecId],
    queryClient,
    enabled: !!mlsMatchError && !!mlsMatchByDate?.planned_kickoff_time,
    onSchedule: ({ scheduledRefetchTime }) => {
      console.log(
        `MatchHandler scheduled refetch for match ${matchSportecId} at ${scheduledRefetchTime.toLocaleString()}`
      );
    },
    onRefetch: () => {
      console.log(`MatchHandler executing refetch for match ${matchSportecId}`);
    },
  });

  return mlsMatchByDate || mlsMatch ? children : null;
}

export const parseDateWithBeforeAndAfter = (dateTimeString) => {
  const [dateString] = dateTimeString.split(" ");
  const parsedDate = parse(dateString, 'MM/dd/yyyy', new Date());

  const dayBefore = subDays(parsedDate, 1);
  const dayAfter = addDays(parsedDate, 1);

  return {
    originalDate: format(parsedDate, "yyyy-MM-dd"),
    dayBefore: format(dayBefore, "yyyy-MM-dd"),
    dayAfter: format(dayAfter, "yyyy-MM-dd"),
  };
};

export const dateWithBeforeAndAfter = (date) => {
  const dayBefore = subDays(date, 1);
  const dayAfter = addDays(date, 1);

  return {
    originalDate: format(date, "yyyy-MM-dd"),
    dayBefore: format(dayBefore, "yyyy-MM-dd"),
    dayAfter: format(dayAfter, "yyyy-MM-dd"),
  };
};

export default MatchHandler;
